import { useEffect, useId, useRef, useState } from 'react'
import { ChevronDown, X } from 'lucide-react'
import { fetchYoutubeClips, type YoutubeClip } from './youtube'
import './header.css'

const configuredYoutubeEndpoint =
  typeof import.meta.env.VITE_YOUTUBE_API_URL === 'string'
    ? import.meta.env.VITE_YOUTUBE_API_URL.trim()
    : ''

export function Header({
  timezone,
  home = '/',
  day = '',
  youtubeEndpoint = configuredYoutubeEndpoint,
}: {
  timezone: string
  home?: string
  day?: string
  youtubeEndpoint?: string
}) {
  const navigationId = useId()
  const youtubeId = useId()
  const [navigationOpen, setNavigationOpen] = useState(false)
  const [youtubeOpen, setYoutubeOpen] = useState(false)
  const [clips, setClips] = useState<YoutubeClip[]>([])
  const [help, setHelp] = useState(false)
  const navigationControl = useRef<HTMLDivElement>(null)
  const navigationTrigger = useRef<HTMLButtonElement>(null)
  const youtubeControl = useRef<HTMLDivElement>(null)
  const youtubeTrigger = useRef<HTMLButtonElement>(null)
  const clipCache = useRef(new Map<string, YoutubeClip[]>())
  const endpoint = youtubeEndpoint.trim()

  useEffect(() => {
    setYoutubeOpen(false)
    if (!endpoint || !day) {
      setClips([])
      return
    }
    const key = `${endpoint}\0${day}`
    const cached = clipCache.current.get(key)
    if (cached) {
      setClips(cached)
      return
    }
    setClips([])
    const controller = new AbortController()
    fetchYoutubeClips(endpoint, day, controller.signal)
      .then((items) => {
        if (!controller.signal.aborted) {
          clipCache.current.set(key, items)
          setClips(items)
        }
      })
      .catch(() => {
        if (!controller.signal.aborted) setClips([])
      })
    return () => controller.abort()
  }, [endpoint, day])

  useEffect(() => {
    if (!navigationOpen) return
    function dismiss(event: PointerEvent) {
      if (!navigationControl.current?.contains(event.target as Node))
        setNavigationOpen(false)
    }
    document.addEventListener('pointerdown', dismiss)
    return () => document.removeEventListener('pointerdown', dismiss)
  }, [navigationOpen])

  useEffect(() => {
    if (!youtubeOpen) return
    function dismiss(event: PointerEvent) {
      if (!youtubeControl.current?.contains(event.target as Node)) setYoutubeOpen(false)
    }
    document.addEventListener('pointerdown', dismiss)
    return () => document.removeEventListener('pointerdown', dismiss)
  }, [youtubeOpen])

  function closeHelp() {
    setHelp(false)
    navigationTrigger.current?.focus()
  }

  return (
    <header className="app-header">
      <div className="identity">
        <a className="brand-link" href={home}>
          <img
            className="brand-logo"
            src="/vod-logo.svg"
            alt="VOD"
            width="117"
            height="28"
          />
        </a>
      </div>
      <div className="top-actions">
        <span className="timezone-tag">Belton, MO ({timezone})</span>
        {clips.length > 0 && (
          <div
            className="youtube-control"
            ref={youtubeControl}
            onBlur={(event) => {
              if (!event.currentTarget.contains(event.relatedTarget))
                setYoutubeOpen(false)
            }}
            onKeyDown={(event) => {
              if (event.key === 'Escape') {
                event.preventDefault()
                event.stopPropagation()
                setYoutubeOpen(false)
                youtubeTrigger.current?.focus()
              }
            }}
          >
            <button
              className="youtube-trigger"
              ref={youtubeTrigger}
              aria-label="YouTube clips"
              aria-expanded={youtubeOpen}
              aria-controls={youtubeId}
              onClick={() => {
                setNavigationOpen(false)
                setYoutubeOpen(!youtubeOpen)
              }}
            >
              <svg
                viewBox="0 0 24 24"
                width="20"
                height="20"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.9"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M2.5 17a24.12 24.12 0 0 1 0-10 2 2 0 0 1 1.4-1.4 49.56 49.56 0 0 1 16.2 0A2 2 0 0 1 21.5 7a24.12 24.12 0 0 1 0 10 2 2 0 0 1-1.4 1.4 49.55 49.55 0 0 1-16.2 0A2 2 0 0 1 2.5 17" />
                <path d="m10 15 5-3-5-3v6Z" fill="currentColor" stroke="none" />
              </svg>
            </button>
            {youtubeOpen && (
              <div
                className="youtube-menu"
                id={youtubeId}
                role="region"
                aria-label="YouTube clips"
              >
                <div className="youtube-menu-header">
                  <strong>YouTube</strong>
                  <span>
                    {clips.length} {clips.length === 1 ? 'clip' : 'clips'}
                  </span>
                </div>
                <div className="youtube-clip-list">
                  {clips.map((clip) => (
                    <a
                      key={clip.id}
                      className="youtube-clip"
                      href={clip.url}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {clip.thumbnail && (
                        <img
                          className="youtube-thumb"
                          src={clip.thumbnail}
                          alt=""
                          loading="lazy"
                          width="72"
                          height="40"
                        />
                      )}
                      <div className="youtube-clip-meta">
                        <span className="youtube-clip-title">{clip.title}</span>
                        {(clip.isShort || clip.tags.length > 0) && (
                          <span className="youtube-clip-tags">
                            {clip.isShort ? 'Short' : 'Video'}
                            {clip.tags.length > 0 ? ` · ${clip.tags.join(' ')}` : ''}
                          </span>
                        )}
                      </div>
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
        <div
          className="navigation-control"
          ref={navigationControl}
          onBlur={(event) => {
            if (!event.currentTarget.contains(event.relatedTarget))
              setNavigationOpen(false)
          }}
          onKeyDown={(event) => {
            if (event.key === 'Escape') {
              event.preventDefault()
              event.stopPropagation()
              setNavigationOpen(false)
              navigationTrigger.current?.focus()
            }
          }}
        >
          <button
            className="brand-trigger"
            ref={navigationTrigger}
            aria-label="VOD navigation"
            aria-expanded={navigationOpen}
            aria-controls={navigationId}
            onClick={() => {
              setYoutubeOpen(false)
              setNavigationOpen(!navigationOpen)
            }}
          >
            <ChevronDown size={20} />
          </button>
          {navigationOpen && (
            <nav className="brand-menu" id={navigationId} aria-label="VOD">
              <a href={home}>Now</a>
              <button
                className="shortcuts-link"
                onClick={() => {
                  setNavigationOpen(false)
                  setHelp(true)
                }}
              >
                Shortcuts
              </button>
              <footer>
                <a href="https://j.ohn.sh" target="_blank" rel="noopener">
                  John Sherrell
                </a>
              </footer>
            </nav>
          )}
        </div>
      </div>
      {help && (
        <div
          className="help-panel"
          role="dialog"
          aria-label="Keyboard shortcuts"
          onKeyDown={(event) => {
            if (event.key === 'Escape') {
              event.stopPropagation()
              closeHelp()
            }
          }}
        >
          <div>
            <strong>Shortcuts</strong>
            <button autoFocus aria-label="Close shortcuts" onClick={closeHelp}>
              <X size={18} />
            </button>
          </div>
          <dl>
            <dt>
              <kbd>Space</kbd>
            </dt>
            <dd>Play / pause</dd>
            <dt>
              <kbd>←</kbd> <kbd>→</kbd>
            </dt>
            <dd>Seek one recording minute</dd>
            <dt>
              <kbd>Shift</kbd> + arrows
            </dt>
            <dd>Seek ten minutes</dd>
            <dt>
              <kbd>F</kbd>
            </dt>
            <dd>Fullscreen</dd>
            <dt>
              <kbd>M</kbd>
            </dt>
            <dd>Mute</dd>
            <dt>
              <kbd>C</kbd>
            </dt>
            <dd>Open calendar</dd>
            <dt>Scroll / pinch</dt>
            <dd>Zoom the timeline</dd>
            <dt>Shift-drag</dt>
            <dd>Pan the timeline</dd>
          </dl>
        </div>
      )}
    </header>
  )
}
